import { alpha, createTheme } from '@mui/material/styles';

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_COLLAPSED_WIDTH = 72;
// Tables scroll inside this height so their sticky header stays in view (the app header, page title and filters sit
// above)
export const TABLE_MAX_HEIGHT = 'max(360px, calc(100vh - 372px))';

// Colours of sponsortrialx's lib/web/frontend/src/theme_new
const COLORS = {
  primary: '#0D2ED3',
  primaryLight: '#4F6DFF',
  primaryDark: '#06188C',
  success: '#007F59',
  warning: '#D89028',
  error: '#EA4335',
  errorDark: '#C62A1F',
  info: '#4F6DFF',
  text: '#252D52', // monochromeBlack
  textSecondary: '#646A84', // OffBlack
  heading: '#0F172B', // DeepNavy
  cellText: '#252C51', // SlateGrey1
  border: '#E2E8F0', // GrayBorder
  tableHead: '#EBF3FD', // CrystalBlue
  rowStripe: '#F7F7FA', // OffWhite
  rowHover: '#EAEEFF', // IceBlue
  rowHighlight: '#D7DEFF', // PeriwinkleLight
  page: '#F7F8FF', // LighterPrimary
  sidebar: '#F8F9FA',
  sidebarText: '#333333', // NeutralDark
  sidebarActive: '#E7EAFB', // LavenderMist
};

const theme = createTheme({
  palette: {
    primary: { main: COLORS.primary, light: COLORS.primaryLight, dark: COLORS.primaryDark },
    success: { main: COLORS.success },
    warning: { main: COLORS.warning },
    error: { main: COLORS.error, dark: COLORS.errorDark },
    info: { main: COLORS.info },
    text: { primary: COLORS.text, secondary: COLORS.textSecondary },
    divider: COLORS.border,
    background: { default: COLORS.page, paper: '#ffffff' },
    sidebar: {
      main: COLORS.sidebar, text: COLORS.sidebarText, active: COLORS.sidebarActive,
      hover: alpha(COLORS.primary, 0.05),
    },
    table: { head: COLORS.tableHead, rowHover: COLORS.rowHover },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    fontSize: 14,
    h5: { color: COLORS.heading },
    h6: { color: COLORS.heading },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  components: {
    MuiCard: { defaultProps: { variant: 'outlined' }, styleOverrides: { root: { borderRadius: 12 } } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 5, fontSize: 13, lineHeight: '20px' },
        contained: { boxShadow: '0px 2px 4px 0px rgba(37, 45, 82, 0.06)' },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: 5 } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: COLORS.border, '&:first-of-type': { paddingLeft: 20 } },
        head: {
          backgroundColor: COLORS.tableHead,
          color: COLORS.cellText,
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          borderBottom: `1px solid ${COLORS.border}`,
        },
        body: { color: COLORS.cellText, fontSize: 14 },
        stickyHeader: { backgroundColor: COLORS.tableHead },
      },
    },
    // Stripes, hover and the highlight (className "highlighted") colour the cells, not the row, so a frozen first
    // column (StickyTableContainer) stays opaque and matches its row
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& > .MuiTableRow-root > .MuiTableCell-body': {
            backgroundColor: '#ffffff', transition: 'background-color 0.3s',
          },
          '& > .MuiTableRow-root:nth-of-type(even) > .MuiTableCell-body': { backgroundColor: COLORS.rowStripe },
          '& > .MuiTableRow-hover:hover > .MuiTableCell-body': { backgroundColor: COLORS.rowHover },
          '& > .MuiTableRow-root.highlighted > .MuiTableCell-body': { backgroundColor: COLORS.rowHighlight },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&:hover': { color: COLORS.primary },
          '&.Mui-active': { color: COLORS.primary, '& .MuiTableSortLabel-icon': { color: COLORS.primary } },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        // both labels are <p>: drop the browser margins so they line up with the rows per page select
        selectLabel: { margin: 0 },
        displayedRows: { margin: 0, color: COLORS.textSecondary },
      },
    },
    MuiChip: { styleOverrides: { sizeSmall: { fontWeight: 500 } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 6, '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } },
      },
    },
  },
});

export default theme;
